
import React, { useState, useEffect, useCallback } from 'react';
import { Appointment, Employee } from '../types';
import { Button } from './Button';
import { DEFAULT_APPOINTMENT_SLOTS, DEFAULT_APPOINTMENT_DURATIONS } from '../constants';
import type { AppointmentModalData } from '../types';

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveAppointment: (appointmentData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'> | Appointment, id?: string) => void;
  initialData?: AppointmentModalData;
  employees: Employee[];
  existingAppointments: Appointment[];
}

export const AppointmentModal: React.FC<AppointmentModalProps> = ({
  isOpen,
  onClose,
  onSaveAppointment,
  initialData = {},
  employees,
  existingAppointments
}) => {
  const [formData, setFormData] = useState<Partial<Appointment>>(initialData.appointment || {});
  const [errors, setErrors] = useState<Partial<Record<keyof Appointment, string>>>({});

  useEffect(() => {
    if (isOpen) {
      const defaultStartTime = DEFAULT_APPOINTMENT_SLOTS[0];
      const defaultDuration = DEFAULT_APPOINTMENT_DURATIONS[0].value;
      
      let baseData: Partial<Appointment> = {
        startTime: defaultStartTime,
        durationMinutes: defaultDuration,
        ...initialData.appointment, // Existing appointment data takes precedence
      };

      // If not editing, apply defaults from ticket or date click
      if (!initialData.appointment) {
        if (initialData.ticketId) baseData.ticketId = initialData.ticketId;
        if (initialData.customerName) baseData.customerName = initialData.customerName;
        if (initialData.date) baseData.date = initialData.date;
      }

      // Ensure date is set if not already
      if (!baseData.date) {
        baseData.date = new Date().toISOString().split('T')[0]; // Default to today
      }
      
      setFormData(baseData);
      setErrors({});
    }
  }, [isOpen, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let processedValue: string | number | undefined = value;
    if (name === 'durationMinutes') {
      processedValue = parseInt(value, 10);
    }
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    if (errors[name as keyof Appointment]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const calculateEndTime = useCallback((startTime?: string, durationMinutes?: number): string => {
    if (!startTime || typeof durationMinutes !== 'number') return '';
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date(0, 0, 0, hours, minutes); // Use a dummy date
    startDate.setMinutes(startDate.getMinutes() + durationMinutes);
    return `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof Appointment, string>> = {};
    if (!formData.customerName?.trim()) newErrors.customerName = 'Customer name is required.';
    if (!formData.serviceDescription?.trim()) newErrors.serviceDescription = 'Service description is required.';
    if (!formData.date) newErrors.date = 'Date is required.';
    if (!formData.startTime) newErrors.startTime = 'Start time is required.';
    if (!formData.durationMinutes) newErrors.durationMinutes = 'Duration is required.';

    // Check for technician availability (simple check, no complex conflict resolution for now)
    if (formData.assignedTechnicianId && formData.date && formData.startTime && formData.durationMinutes) {
      const newStartTime = new Date(`${formData.date}T${formData.startTime}:00`).getTime();
      const newEndTime = newStartTime + formData.durationMinutes * 60000;

      const conflict = existingAppointments.find(apt => {
        if (apt.id === formData.id) return false; // Don't compare with itself if editing
        if (apt.assignedTechnicianId !== formData.assignedTechnicianId) return false;
        if (apt.date !== formData.date) return false;

        const existingStartTime = new Date(`${apt.date}T${apt.startTime}:00`).getTime();
        const existingEndTime = new Date(`${apt.date}T${apt.endTime}:00`).getTime();
        
        // Check for overlap
        return (newStartTime < existingEndTime && newEndTime > existingStartTime);
      });

      if (conflict) {
        newErrors.assignedTechnicianId = `Technician is already booked for ${conflict.startTime} - ${conflict.endTime}.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const endTime = calculateEndTime(formData.startTime, formData.durationMinutes);
      const finalData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'> | Appointment = {
        ...formData,
        customerName: formData.customerName!,
        serviceDescription: formData.serviceDescription!,
        date: formData.date!,
        startTime: formData.startTime!,
        durationMinutes: formData.durationMinutes!,
        endTime,
        // Conditional ID for new vs update
        ...(formData.id ? { id: formData.id, createdAt: formData.createdAt!, updatedAt: new Date().toISOString() } : {}),
      };
      onSaveAppointment(finalData, formData.id);
      onClose();
    }
  };

  if (!isOpen) return null;

  const renderError = (field: keyof Appointment) => {
    return errors[field] ? <p className="text-red-500 text-xs mt-1">{errors[field]}</p> : null;
  };

  const inputClass = "mt-1 block w-full form-input";
  const labelClass = "block text-sm font-medium text-slate-700";
  const endTimeDisplay = calculateEndTime(formData.startTime, formData.durationMinutes);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[120]"
      aria-labelledby="appointment-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 border-b pb-3">
          <h2 id="appointment-modal-title" className="text-xl font-semibold text-slate-800">
            {formData.id ? 'Edit Appointment' : 'Book New Appointment'}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700" aria-label="Close modal">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="customerName" className={labelClass}>Customer Name <span className="text-red-500">*</span></label>
            <input type="text" name="customerName" id="customerName" value={formData.customerName || ''} onChange={handleChange} className={inputClass} required />
            {renderError('customerName')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label htmlFor="ticketId" className={labelClass}>Ticket ID (Optional)</label>
                <input type="text" name="ticketId" id="ticketId" value={formData.ticketId || ''} onChange={handleChange} className={inputClass} placeholder="e.g. TM-001"/>
            </div>
             <div>
                <label htmlFor="serviceDescription" className={labelClass}>Service / Reason <span className="text-red-500">*</span></label>
                <input type="text" name="serviceDescription" id="serviceDescription" value={formData.serviceDescription || ''} onChange={handleChange} className={inputClass} required placeholder="e.g. iPhone Screen Repair, Consultation" />
                {renderError('serviceDescription')}
            </div>
          </div>


          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label htmlFor="date" className={labelClass}>Date <span className="text-red-500">*</span></label>
              <input type="date" name="date" id="date" value={formData.date || ''} onChange={handleChange} className={inputClass} required min={new Date().toISOString().split('T')[0]} />
              {renderError('date')}
            </div>
            <div>
              <label htmlFor="startTime" className={labelClass}>Start Time <span className="text-red-500">*</span></label>
              <select name="startTime" id="startTime" value={formData.startTime || ''} onChange={handleChange} className={inputClass} required>
                <option value="">Select slot</option>
                {DEFAULT_APPOINTMENT_SLOTS.map(slot => <option key={slot} value={slot}>{slot}</option>)}
              </select>
              {renderError('startTime')}
            </div>
             <div>
              <label htmlFor="durationMinutes" className={labelClass}>Duration <span className="text-red-500">*</span></label>
              <select name="durationMinutes" id="durationMinutes" value={formData.durationMinutes || ''} onChange={handleChange} className={inputClass} required>
                {DEFAULT_APPOINTMENT_DURATIONS.map(dur => <option key={dur.value} value={dur.value}>{dur.label}</option>)}
              </select>
              {renderError('durationMinutes')}
            </div>
          </div>
           {endTimeDisplay && (
             <p className="text-sm text-slate-600 text-right -mt-2">Estimated End Time: <span className="font-semibold">{endTimeDisplay}</span></p>
           )}

          <div>
            <label htmlFor="assignedTechnicianId" className={labelClass}>Assigned Technician (Optional)</label>
            <select name="assignedTechnicianId" id="assignedTechnicianId" value={formData.assignedTechnicianId || ''} onChange={handleChange} className={inputClass}>
              <option value="">Unassigned / Any</option>
              {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name} ({emp.role})</option>)}
            </select>
             {renderError('assignedTechnicianId')}
          </div>

          <div>
            <label htmlFor="notes" className={labelClass}>Notes (Optional)</label>
            <textarea name="notes" id="notes" rows={3} value={formData.notes || ''} onChange={handleChange} className={inputClass} placeholder="e.g., Customer requested early drop-off if possible."></textarea>
          </div>

          <div className="border-t border-slate-200 pt-4 flex justify-end space-x-3">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary">{formData.id ? 'Save Changes' : 'Book Appointment'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};
